import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { typesForRegister } from "routes/asset-register/plugins/asset-mapping/controller/types-for-register"
import { useSearchParam } from "lib/@hooks/use-search-param"
import {
    Box,
    Card,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    Button,
} from "@mui/material"
import { Bound } from "lib/@components/binding/Bound"
import { ListItemBox } from "lib/@components/ListItemBox"
import { MappingActions } from "routes/asset-register/plugins/asset-mapping/mapping-actions"
import { SearchBoxUsingSearchParam } from "lib/@components/searchBoxUsingSearchParam"
import { NoResults } from "lib/@components/no-results/no-results"
import { MapAssetTypeToLibrary } from "routes/asset-register/plugins/asset-mapping/map-asset-type-to-library"
import { setFromEvent } from "lib/setFromEvent"
import { withBusyMessage } from "lib/graphql/with-busy-message"
import { emptyArray } from "lib/empty-array"
import { Loader } from "lib/@components/Loader"
import { IS_LOADING, returnIsLoadingOnEachRequest } from "lib/graphql/return-is-loading-on-each-request"
import { StandardCombo } from "lib/@components/combos/standardCombo"
import noop from "lib/noop"
import Iconify from "minimals-template/components/Iconify"
import { useEffect, useState } from "react"

import { refreshId } from "lib/graphql/api"
import { useIsAnythingRefining } from "routes/asset-register/plugins/asset-mapping/use-is-anything-refining"
import { navigate } from "lib/routes/navigate"
import { OpenMappingSettings } from "routes/asset-register/plugins/asset-mapping/settings-dialog"
import { useReferenceState } from "lib/@hooks/use-reference"
import { defaultNumberOfRecords } from "lib/@components/pagination/default-number-of-records"
import { uniqueId } from "library/database/split-id.mjs"
import { RefiningTypes } from "library/server-event-definitions/shared-events.mjs"
import { PaginationRow } from "./lib/pagination-row"

export function AllMappings() {
    const { target } = useBoundContext()
    const registerId = target?._id ?? target?.id
    const registerKey = registerId ? uniqueId(registerId) : null
    const [search] = useSearchParam("search")
    const [busy, setBusy] = useState(false)

    const { isManualMapTab } = useBoundContext()
    const [filter, setFilter] = useSearchParam("filter", "all")
    const [sort, setSort] = useSearchParam("sort", "type")
    const filterOptions = buildFilterOptions()
    const activeFilter = filterOptions.some((option) => option.value === filter) ? filter : "all"

    useIsAnythingRefining()
    RefiningTypes.useEvent(handleRefiningTypesUpdate, [registerKey])

    const [types, refresh] = typesForRegister.useResults.withRefetch(
        refreshId(target.id),
        search,
        activeFilter,
        sort,
        returnIsLoadingOnEachRequest(),
        withBusyMessage("Loading types", true)
    ) ?? [emptyArray, noop]

    // const progress = getRegisterProgress.useResults(target?._id)
    // useEffect(() => {
    //     setBusy(!!progress)
    // }, [progress])

    const actions = MappingActions.useItems()
    const typeList = Array.isArray(types) ? types : emptyArray
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useReferenceState("asset-mapping-types-page-size", defaultNumberOfRecords[1] ?? 10)

    useEffect(resetPageWhenQueryChanges, [search, filter, sort])
    useEffect(clampPageToAvailableResults, [typeList, page, pageSize])

    const pagedTypes = typeList.filter(Boolean).slice((page - 1) * pageSize, page * pageSize)
    const pageCount = Math.max(1, Math.ceil(typeList.filter(Boolean).length / pageSize))
    const missingFallbackTypes =
        (target?.options?.originalTypes &&
            typeList.filter((t) => typeof t === "string" && t.startsWith("Unknown Type"))) ||
        emptyArray

    if (missingFallbackTypes.length > 0) {
        const samples = deriveMissingSamples(missingFallbackTypes)
        return (
            <Card sx={{ p: 3, mb: 2 }}>
                <Stack spacing={2}>
                    <Typography variant="h6">Asset Types Missing</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Some assets have no "Asset Type" while "Use Unrefined Types" is enabled. Add Asset Type values
                        in your import and re-import, or switch off "Use Unrefined Types" to continue.
                    </Typography>
                    <Stack spacing={0.5}>
                        <Typography variant="subtitle2">Examples</Typography>
                        {samples.map((sample) => (
                            <Typography key={sample} variant="body2">
                                {sample}
                            </Typography>
                        ))}
                    </Stack>
                    <Stack direction="row" spacing={1}>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                const registerId = target?.id ?? target?._id
                                if (!registerId) return
                                navigate(`/app/assets/${encodeURIComponent(registerId)}?asset-tabsTab=imported`)
                            }}
                            data-cy="missing-type-reimport"
                        >
                            Re-import with Asset Type
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => OpenMappingSettings.raise()}
                            data-cy="missing-type-settings"
                        >
                            Open Mapping Settings
                        </Button>
                    </Stack>
                </Stack>
            </Card>
        )
    }

    return types === IS_LOADING || busy ? (
        <Loader inline color="primary" caption="Retrieving types..." />
    ) : (
        <Bound allTypes={typeList.filter(Boolean)}>
            <Card sx={{ p: 4.5, mb: 3, mt: 2, overflow: "visible" }}>
                <Stack spacing={1}>
                    {!!actions.length && <ListItemBox>{actions}</ListItemBox>}

                    <ListItemBox spacing={1} sx={{ "& *": { fontSize: 14 } }}>
                        <SearchBoxUsingSearchParam
                            placeholder="Search..."
                            variant="outlined"
                            startAdornment={<Iconify icon="iconamoon:search" width="24px" height="24px" />}
                            sx={{ flex: 1, py: 0, "& *": { fontSize: 14 } }}
                            textFieldProps={{
                                inputProps: {
                                    sx: { py: 1, borderRadius: 1 },
                                    "aria-label": "Search bar for asset type mappings",
                                },
                            }}
                        />
                        <Box data-cy="asset-register-map-types-mappings-filter-buttons">
                            <StandardCombo
                                label="Status"
                                size="small"
                                fullWidth={false}
                                value={activeFilter}
                                onChange={setFromEvent(setFilter)}
                                sx={{ width: 200 }}
                                options={filterOptions}
                            />
                        </Box>
                        <Box data-cy="asset-register-map-types-mappings-sort-buttons">
                            <StandardCombo
                                label="Sort"
                                size="small"
                                fullWidth={false}
                                value={sort}
                                onChange={setFromEvent(setSort)}
                                sx={{ width: 90 }}
                                options={[
                                    { label: "Type", value: "type" },
                                    { label: "Count", value: "count" },
                                ]}
                            />
                        </Box>
                        <Box>
                            <IconButton
                                aria-label="Refresh mappings"
                                size="small"
                                color="primary"
                                onClick={refresh}
                                data-cy="refresh-mappings"
                            >
                                <Iconify icon="mdi:refresh" />
                            </IconButton>
                        </Box>
                        <Box display="flex" justifyContent="flex-end">
                            <PaginationRow
                                compact
                                page={page}
                                pageCount={pageCount}
                                pageSize={pageSize}
                                onChange={setPage}
                                onPageSizeChange={(value) => {
                                    setPageSize(value)
                                    setPage(1)
                                }}
                            />
                        </Box>
                    </ListItemBox>

                    <Table
                        stickyHeader
                        size="small"
                        sx={{
                            "& > thead > tr > th": {
                                py: 1.5,
                            },
                            "& > tbody > tr > td": {
                                py: 1.5,
                                borderBottom: (theme) => "1px solid " + theme.palette.action.focus,
                            },
                        }}
                        data-cy="asset-register-map-types-mappings-table"
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell></TableCell>
                                <TableCell>Asset Type</TableCell>
                                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                    Count
                                </TableCell>
                                <TableCell>Schedule Mapping</TableCell>
                                <TableCell align="right">Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {pagedTypes.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5}>
                                        <NoResults>{noResultsMessage()}</NoResults>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                pagedTypes.map((type) => <MapAssetTypeToLibrary key={type} item={type} />)
                            )}
                        </TableBody>
                    </Table>
                    <PaginationRow
                        page={page}
                        pageCount={pageCount}
                        pageSize={pageSize}
                        onChange={setPage}
                        onPageSizeChange={(value) => {
                            setPageSize(value)
                            setPage(1)
                        }}
                        dataCy="paged-repeat-pagination-bottom"
                    />
                </Stack>
            </Card>
        </Bound>
    )

    function resetPageWhenQueryChanges() {
        setPage(1)
    }

    function clampPageToAvailableResults() {
        const maxPage = Math.max(1, Math.ceil(typeList.filter(Boolean).length / pageSize))
        if (page > maxPage) setPage(maxPage)
    }

    function handleRefiningTypesUpdate(eventRegisterId, isBusy) {
        if (!registerKey || !eventRegisterId) {
            return
        }

        if (uniqueId(eventRegisterId) === registerKey) {
            setBusy(!!isBusy)
        }
    }

    function noResultsMessage() {
        if (!["origin-auto", "origin-assisted"].includes(activeFilter)) return "No Results"
        return "No mappings with this origin. Older mappings appear under Done."
    }

    function buildFilterOptions() {
        return [
            { label: "All", value: "all" },
            { label: "To Do", value: "not mapped" },
            isManualMapTab && { label: "Mapped", value: "mapped" },
            !isManualMapTab && { label: "Done", value: "mapped" },
            !isManualMapTab && { label: "Mapped (Auto)", value: "origin-auto" },
            !isManualMapTab && { label: "Mapped (AI-assisted)", value: "origin-assisted" },
            !isManualMapTab && { label: "Mapped (Manual)", value: "require custom" },
            { label: "Excluded", value: "do not map" },
        ].filter(Boolean)
    }
}

function deriveMissingSamples(fallbackTypes = []) {
    const samples = []
    for (const type of fallbackTypes) {
        const match = /\(([^)]+)\)/.exec(type)
        const listString = match?.[1] ?? ""
        const primaryIds = listString.split("+")[0] || listString
        const tagRegex = /\[_\|]([^:]+):?/g
        let tagMatch
        while ((tagMatch = tagRegex.exec(primaryIds))) {
            const tag = (tagMatch[1] || "").trim()
            if (tag) samples.push(tag)
        }
        if (samples.length === 0 && match) {
            samples.push(primaryIds || type)
        }
        if (!match) samples.push(type)
        if (samples.length >= 8) break
    }
    return samples.unique()
}
